import React from "react";
import { HoverDeleteButton } from "@scripts/react/components/Button";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { MediaItemFileType } from "@rapid/types/media";

/**
 * Full loading progress for single file view - shows text, progress bar, and percentage
 */
function LoadingProgressInfo({
    uploadPhaseText,
    uploadProgress
}: {
    uploadPhaseText?: string;
    uploadProgress?: number;
}) {
    return (
        <div className="flex w-full flex-col items-center gap-1">
            {uploadPhaseText && <span className="text-xs text-tertiary">{uploadPhaseText}</span>}
            {uploadProgress !== undefined && uploadProgress >= 0 && (
                <>
                    <div className="h-1 w-full overflow-hidden rounded-full bg-gray-200">
                        <div
                            className="h-full bg-brand transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                        />
                    </div>
                    <span className="text-xs text-tertiary">{uploadProgress}%</span>
                </>
            )}
        </div>
    );
}

/**
 * Shared component for rendering thumbnail content (image/spinner/icon) with container
 * Used by both FileSwitcher and FileGridPreview
 */
export function ThumbnailContent({
    thumbnail,
    isUploading,
    fileType,
    fileName,
    fileId,
    className,
    style,
    title,
    onClick,
    onKeyDown,
    onDoubleClick,
    onDelete,
    onEdit,
    darkMode = false,
    isSelected = false,
    isDisabled = false,
    isSingleFile = false,
    showPlayButton = false,
    uploadProgress,
    uploadPhaseText,
    isApproved = false
}: {
    thumbnail: string | undefined;
    isUploading: boolean;
    fileType: MediaItemFileType;
    fileName: string;
    fileId?: string;
    className?: string;
    style?: React.CSSProperties;
    title?: string;
    onClick?: (evt: React.MouseEvent) => void;
    onKeyDown?: (evt: React.KeyboardEvent) => void;
    onDoubleClick?: (evt: React.MouseEvent) => void;
    onDelete?: (evt: React.MouseEvent) => void;
    onEdit?: (evt: React.MouseEvent) => void;
    darkMode?: boolean;
    isSelected?: boolean;
    isDisabled?: boolean;
    isSingleFile?: boolean;
    showPlayButton?: boolean;
    uploadProgress?: number;
    uploadPhaseText?: string;
    isApproved?: boolean;
}) {
    const imageClassName = "h-full w-full rounded-md object-cover";
    const spinnerClassName = "fa fa-spinner fa-spin text-sm text-gray-500";
    const iconClassName = `fa ${fileType === "video" ? "fa-spinner fa-spin" : "fa-image"} text-sm text-gray-500`;

    const sharedClasses = twClassNames(
        "relative flex flex-shrink-0 flex-col items-center justify-center rounded-md bg-gray-100 text-tertiary shrink-0 aspect-square transition-all duration-75",
        "focus:outline-none focus:ring-2",
        isDisabled && "cursor-not-allowed opacity-70 ring-1",
        isSelected && "ring-2",
        !isSelected && !isDisabled && "cursor-pointer ring-1 hover:ring-2",
        darkMode
            ? {
                  "ring-gray-600": isDisabled,
                  "ring-purboflow-400": isSelected,
                  "ring-gray-600 hover:ring-gray-500 focus:ring-gray-500":
                      !isSelected && !isDisabled
              }
            : {
                  "ring-gray-300": isDisabled,
                  "ring-brand": isSelected,
                  "ring-gray-300 hover:ring-gray-400 focus:ring-gray-400":
                      !isSelected && !isDisabled
              }
    );
    let content;
    if (thumbnail) {
        content = (
            <div className="relative h-full w-full">
                <img src={thumbnail} alt={fileName} className={imageClassName} />
                {(isUploading || isApproved) && (
                    <div
                        className={twClassNames(
                            "pointer-events-none absolute inset-0 flex h-full w-full items-center justify-center rounded-md shadow-md",
                            isUploading ? "bg-black/30" : "bg-black/50"
                        )}
                    >
                        {isUploading ? (
                            <i className="fas fa-spinner fa-spin text-xl text-white" />
                        ) : (
                            <i className="fas fa-check text-xl text-white" />
                        )}
                    </div>
                )}
            </div>
        );
    } else {
        const icon = isUploading ? spinnerClassName : iconClassName;

        content = (
            <div
                className={twClassNames(
                    "flex h-full w-full flex-col items-center justify-center",
                    isSingleFile ? "gap-2 p-2 px-8" : "gap-0.5 p-1"
                )}
            >
                {!isSingleFile && isUploading && <i className={icon} />}
                {isSingleFile && isUploading && (
                    <LoadingProgressInfo
                        uploadPhaseText={uploadPhaseText}
                        uploadProgress={uploadProgress}
                    />
                )}
            </div>
        );
    }

    return (
        <div
            className={twClassNames("group", sharedClasses, className)}
            style={style}
            role={onClick ? "button" : undefined}
            tabIndex={onClick && !isDisabled ? 0 : -1}
            onClick={onClick}
            onKeyDown={
                onKeyDown ??
                (onClick
                    ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              onClick(e as unknown as React.MouseEvent);
                          }
                      }
                    : undefined)
            }
            onDoubleClick={onDoubleClick}
            title={title}
            aria-label={title || fileName}
            data-file-id={fileId}
        >
            {content}
            {/* Hover action buttons container - positioned at top-right */}
            {!isUploading && (onEdit || onDelete) && (
                <div className="absolute -right-1 -top-1 z-10 flex items-center gap-1 opacity-0 transition duration-75 group-hover:opacity-100">
                    {onEdit && (
                        <button
                            onClick={onEdit}
                            className="flex h-4 w-4 cursor-pointer items-center justify-center rounded-full bg-gray-600 text-xs text-white shadow-sm ring-1 ring-inset ring-white ring-opacity-50 transition duration-75 hover:bg-gray-500 focus:outline-none"
                            aria-label={`Rename ${fileName}`}
                        >
                            <i className="far fa-pencil text-[8px]" />
                        </button>
                    )}
                    {onDelete && (
                        <HoverDeleteButton
                            onClick={onDelete}
                            ariaLabel={`Delete ${fileName}`}
                            className="!relative !opacity-100"
                        />
                    )}
                </div>
            )}
            {!isDisabled && (
                <div
                    className={twClassNames(
                        "absolute inset-0 rounded-md bg-black/20 opacity-0 transition duration-75",
                        !isDisabled && "group-hover:opacity-100",
                        isSelected && "bg-brand/30 opacity-100"
                    )}
                />
            )}
            {showPlayButton && thumbnail && !isUploading && (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-black bg-opacity-60 backdrop-blur-sm transition-all duration-200 group-hover:scale-110 group-hover:bg-opacity-80">
                        <i className="fa fa-play ml-1 text-2xl text-white" />
                    </div>
                </div>
            )}
        </div>
    );
}
